import React, { useState, useEffect } from "react";
import { Table, Spinner } from "react-bootstrap";
import { connect } from 'react-redux';
import FarmSubscriptionsHead from './farmSubscriptionsHead';
import FarmSubscriptionsItem from './farmSubscriptionsItem';
import siteService from '../../sites/duck/siteService';
import InfoDialogue from '../../group/InvitationModal/inviteDone';
import Confirm, { useConfirm } from '../../common/confirm';

function FarmSubscriptions({
    name,
    farmId,
    search,
    planFilters,
    activeFilter,
    isEditMode,
    originalPlans,
    parent,
    sources,
    openDoneModal,
    onLastFarmIsRendered,
    matchedSitesCount,
    setMatchedSitesCount,
    activeNewSubscription,
    setActiveNewSubscriptions,
    updatedSites,
    preloadedSites, // NEW: Sites data passed from parent
}) {
    const [sites, setSites] = useState(null);
    const [dialogueBoxSate, setDialogueBoxState] = useState({ show: false, data: { title: '', message: '' } });
    const { confirmBoxSate, askConfirmation } = useConfirm();

    function showDialogue(title, message) {
        setDialogueBoxState({ show: true, data: { title, message } });
    }

    function closeDialogue() {
        setDialogueBoxState(prev => ({ ...prev, show: false }));
    }

    function handleStatusChange(siteObjectId, newStatus) {
        // Update the local sites state to reflect the new status
        setSites(prevSites => {
            if (!prevSites) return prevSites;
            return prevSites.map(site =>
                site.id === siteObjectId
                    ? { ...site, status: newStatus }
                    : site
            );
        });
    }

    useEffect(() => {
        // OPTIMIZED: Use preloaded data if available, otherwise fetch from API
        if (preloadedSites !== null) {
            // Use the data already fetched by parent component
            setSites(preloadedSites);
        } else if (preloadedSites === null && sites === null) {
            // Fallback: Only fetch if we don't have preloaded data
            // This ensures backward compatibility if preloadedSites isn't passed
            siteService.getSiteDetailsWithSubscriptions(farmId, false)
                .then(response => setSites(response.data))
                .catch(error => console.log(error));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [farmId, preloadedSites]);

    const matchedSites = sites && sites.filter(site => {
        // Text search filter
        const matchesSearch = !search ||
            site.siteId?.startsWith(search) ||
            site.name?.toLowerCase().includes(search.toLowerCase()) ||
            site.dealerName?.toLowerCase().includes(search.toLowerCase());

        // Plan filter
        const sitePlan = site.planDetails?.plan || site.plan || 'None';
        const matchesPlan = planFilters ? planFilters[sitePlan] : true;

        // Active filter
        const isActive = site.status === 'ACTIVE';
        const matchesActive = !activeFilter || activeFilter === 'all' ||
            (activeFilter === 'active' && isActive) ||
            (activeFilter === 'inactive' && !isActive);

        return matchesSearch && matchesPlan && matchesActive;
    });

    useEffect(() => {
        if (search && sites && onLastFarmIsRendered) {
            onLastFarmIsRendered();
        }
    })

    // Hide farm block if no sites match the filters
    const hasActiveFilters = search ||
        (planFilters && Object.values(planFilters).some(v => !v)) ||
        (activeFilter && activeFilter !== 'all');

    if (hasActiveFilters && sites && matchedSites.length === 0) {
        return null;
    }

    if (sites === null) {
        return (
            <React.Fragment>
                <h6>{name}</h6>
                <Spinner animation="grow" />
            </React.Fragment>
        )
    }

    if (sites && sites.length === 0) {
        return (
            <React.Fragment>
                <h6>{name}</h6>
                <small>This farm has no sites</small>
            </React.Fragment>
        )
    }

    return (
        <React.Fragment>
            <h6>{name}</h6>
            <Table striped bordered hover responsive="sm">
                <FarmSubscriptionsHead isEditMode={isEditMode} />
                <tbody>
                    {matchedSites.map(site => <FarmSubscriptionsItem
                        key={site.siteId}
                        siteId={site.siteId}
                        objectId={site.id}
                        name={site.name}
                        subscription={
                            site.planDetails?.plan
                        }
                        status={site.status}
                        isEditMode={isEditMode}
                        originalPlan={originalPlans?.[site.siteId]}
                        openDoneModal={openDoneModal}
                        parent={parent}
                        showDialogue={showDialogue}
                        askConfirmation={askConfirmation}
                        activeNewSubscription={activeNewSubscription}
                        setActiveNewSubscriptions={setActiveNewSubscriptions}
                        onStatusChange={handleStatusChange}
                    />)}
                </tbody>
            </Table>
            <InfoDialogue
                show={dialogueBoxSate.show}
                onClose={closeDialogue}
                buttonLabel="OK"
                data={dialogueBoxSate.data} />
            <Confirm
                show={confirmBoxSate.show}
                title={confirmBoxSate.data.title}
                message={confirmBoxSate.data.message}
                onYes={confirmBoxSate.onYes}
                onNo={confirmBoxSate.onNo} />
        </React.Fragment>
    )
}

const mapStateToProps = state => {

    const customer = state.userDetails.profileDetails.profile?.stripeCustomerDetails;

    if (!customer) {
        return { sources: null }
    }

    return { sources: customer.sources?.data };
}

export default connect(mapStateToProps)(FarmSubscriptions);